import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import type { BookingFlag } from '../bookings/flags';

// The flags that read as a WORD instead of a glyph, and the word each reads.
// Sentence case, one word: "Viewing" is exactly as "New" is.
const TEXT_BADGES: Partial<Record<BookingFlag['kind'], string>> = {
  'first-booking': 'New',
  viewing: 'Viewing',
};

export interface FlagStripProps {
  flags: BookingFlag[];
  // Prefix the dog name in the hover title — for combined strips where the icon
  // is not sitting next to the dog's name (e.g. a household-level strip).
  attributed?: boolean;
  size?: number;
  // A CEILING, with the remainder counted. The chips cannot shrink — a warning
  // that ellipsises is a warning nobody sees — so on a narrow cell they either
  // take the whole line from the names or vanish off its end. Capped, the strip
  // has a known width, the names keep theirs, and what is hidden is at least
  // ANNOUNCED. Omitted everywhere the cell is wide enough to run free.
  max?: number;
  // FIT THE CEILING TO THE ROOM ACTUALLY LEFT, rather than to a breakpoint.
  // `max` is a tier's guess at how many chips the cell can hold, and a guess
  // cannot know that this row's dog is called "Bo" — so a short name left the
  // line half empty beside a counter (Sir, 2026-08-27). With this set the strip
  // measures what is in front of it and draws as many as fit.
  fitToSpace?: boolean;
}

// The at-a-glance booking-flag strip. Each flag is a tinted chip whose glyph is
// the Icon of the same name; the full detail is revealed on hover/focus via the
// native title (robust in dense, overflow-clipped table cells where a bespoke
// popover would be clipped). One presentational component, shared by the daily
// desk, the bookings table, and the booking detail.
/**
 * How many chips fit in the space to the right of whatever precedes the strip.
 *
 * Measured, for the same reason the names are: the chips cannot shrink and CSS
 * cannot count. A hidden copy holding every chip is laid out and each one's
 * right edge compared with the line's; the visible run is derived once and
 * never observed, so the decision cannot feed its own input.
 */
function useFittedFlags(count: number, enabled: boolean, key: string) {
  const stripRef = useRef<HTMLSpanElement | null>(null);
  const probeRef = useRef<HTMLSpanElement | null>(null);
  // STARTS AT ONE WHERE IT MEASURES. The count is raised by the layout effect
  // before the first paint, so nothing flickers — but if the effect cannot run
  // (a DOM without ResizeObserver, a server render) the strip under-draws
  // rather than over-draws, and a row shows one warning instead of nine hanging
  // off the end of its column.
  const [fit, setFit] = useState(enabled ? 1 : count);
  const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

  useIsomorphicLayoutEffect(() => {
    if (!enabled) return;
    const strip = stripRef.current;
    const probe = probeRef.current;
    const line = strip?.parentElement;
    if (!strip || !probe || !line) return;

    const measure = () => {
      // THE ROOM IS COMPUTED, NOT OBSERVED, and that is what stops it chasing
      // its own tail. Measuring from where the strip currently STARTS reads a
      // position the strip itself just moved: it grew, the names gave way, its
      // left edge slid back, and the next measurement found room that only
      // existed because of the growth. At 1240 that settled on six chips in a
      // line that could hold two, and hung 79px off the end of it.
      //
      // Every term here is independent of the strip. The line's width is the
      // column's. The names' NATURAL width comes from their own hidden probe —
      // what they would take if nothing pushed them. And the reserve is the
      // most of that the names are allowed to keep. So: the room is what is
      // left after the names have had the smaller of what they want and what
      // they are owed.
      const lineBox = line.getBoundingClientRect();
      const style = getComputedStyle(line);
      const reserve = parseFloat(style.getPropertyValue('--names-reserve')) || 0;
      const namesEl = line.querySelector('.dog-names-text') as HTMLElement | null;
      const namesProbe = namesEl?.querySelector('.dog-names-probe') as HTMLElement | null;
      const namesWanted = namesProbe
        ? namesProbe.getBoundingClientRect().width
        : (namesEl?.scrollWidth ?? 0);
      const available = lineBox.width - GAP - Math.min(namesWanted, reserve);
      if (available <= 0) return;
      const chips = [...probe.children] as HTMLElement[];
      if (chips.length === 0) return;
      const origin = probe.getBoundingClientRect().left;
      let fits = 0;
      for (const chip of chips) {
        // Every chip but the last is measured against a line one COUNTER short,
        // since a run that does not finish has to say so.
        const room = fits === chips.length - 1 ? available : available - COUNTER;
        if (chip.getBoundingClientRect().right - origin <= room) fits += 1;
        else break;
      }
      setFit(Math.max(1, fits));
    };

    measure();
    // WATCH EVERY INPUT, not just the line. The fitted count depends on three
    // things: the line's width, the NAMES' natural width, and the chips' own
    // widths — and only the first is fixed by the column. A row refreshed in
    // place with renamed dogs or a different KIND of flag keeps its count, so
    // the observer never fired and the old fit persisted, leaving a warning
    // clipped or a chip needlessly withheld (codex, PR #172).
    //
    // Both probes carry the full-length content, so their boxes change whenever
    // the content does; observing them covers what a resize of the line cannot.
    const observer = new ResizeObserver(measure);
    observer.observe(line);
    observer.observe(probe);
    const namesProbe = line.querySelector('.dog-names-probe');
    if (namesProbe) observer.observe(namesProbe);
    return () => observer.disconnect();
    // `key` re-runs the effect when the flags themselves change identity — a
    // swap of one kind for another at the same count moves no box on its own.
  }, [count, enabled, key]);

  return { fit: enabled ? Math.min(fit, count) : count, stripRef, probeRef };
}

// A "+9" at the strip's own size, plus its gap.
const COUNTER = 26;
// The space between the last name and the first chip — `.dog-names`' gap plus
// the strip's own margin. Mirrors the same 11 in 05-today.css.
const GAP = 11;

export function FlagStrip({
  flags,
  attributed = false,
  size = 13,
  max,
  fitToSpace = false,
}: FlagStripProps) {
  // The hook runs before the early return, because hooks must.
  const fitted = useFittedFlags(
    flags?.length ?? 0,
    fitToSpace,
    (flags ?? []).map((f) => f.kind + (f.dogId ?? '')).join('|'),
  );
  if (!flags || flags.length === 0) return null;
  // The ceiling is what the ROOM allows where the strip measures it, and what
  // the tier allows where it does not.
  const ceiling = fitToSpace ? fitted.fit : max;
  // The counter takes a slot of its own, so overflowing at N shows N-1 chips.
  const overflows = ceiling !== undefined && flags.length > ceiling;
  // TWO, AT THE VERY LEAST, wherever there are two to show (Sir, 2026-08-27).
  // One warning and a count of the rest tells the reader that something matters
  // without telling them enough of what; two is the floor at which the strip
  // starts being useful. Where the room will not stretch to it the NAMES give
  // way — the priority runs tags first, and this is where it is expressed.
  const atLeast = Math.min(2, flags.length);
  // AT ONE SLOT THE SLOT GOES TO A TAG, never to a counter. A lone "+7" tells
  // the reader that something matters without telling them what, and one real
  // warning is worth more than an accurate count of warnings withheld (Sir,
  // 2026-08-27) — so the one slot shows the highest-priority flag and the rest
  // are named in the strip's own title.
  //
  // From two slots up the last one counts what it stands in for: "New +5".
  // …BUT NEVER MORE THAN FITS. The floor forced two chips even where the
  // measurement said one, and with no `max-width` left to stop it the second
  // was simply clipped off the row — a vaccine or a keep-apart gone with no
  // "+N" to say so, which is the one loss this strip must never make silently
  // (claude, PR #172, P1). Where the room will not hold two AND the counter,
  // it holds one and the counter, and the count tells the truth.
  const fits = ceiling ?? flags.length;
  const target = overflows ? Math.max(1, ceiling! - 1) : flags.length;
  const drawn = Math.max(Math.min(atLeast, fits), target);
  const shown = flags.slice(0, Math.min(drawn, flags.length));
  const hidden = flags.length - shown.length;
  const untold = hidden;
  const all = flags.map((f) => `${f.label}: ${f.detail}`).join('\n');
  // THE MEASURING COPY — every chip, laid out and never drawn. It rides in
  // BOTH branches: the compact one is entered when the fit is 1, and a branch
  // that dropped the probe could never be measured back out of it.
  const probe = fitToSpace && flags.length > 1 && (
    <span className="flag-strip-probe" aria-hidden="true" ref={fitted.probeRef}>
      {flags.map((f, i) => (
        <span key={`probe-${i}`} className={TEXT_BADGES[f.kind] ? 'bflag-new' : 'bflag'}>
          {TEXT_BADGES[f.kind] ?? <Icon name={f.icon} size={size} />}
        </span>
      ))}
    </span>
  );

  return (
    <span className="flag-strip" title={untold > 0 ? all : undefined} ref={fitted.stripRef}>
      {shown.map((f, i) => {
        // The dog's name always leads its own chip's title on the desk, where
        // the chip no longer sits beside the dog it is about.
        const who = (attributed || f.scope === 'dog') && f.dogName ? `${f.dogName} — ` : '';
        const title = `${who}${f.label}: ${f.detail}`;
        const aria = `${f.dogName ? f.dogName + ' ' : ''}${f.label}`;
        const key = `${f.kind}-${f.dogId ?? 'h'}-${i}`;
        // A SMALL SET reads as a tiny text badge rather than an icon chip —
        // some marks are a word. The badge geometry was already right when this
        // was `kind === 'first-booking'`; only the membership test was too
        // narrow, so it widened rather than gaining a second badge component.
        // The tint rides on the same class as the icon chips, so a new member
        // supplies a colour and nothing else.
        // ALWAYS THE WORD for New and Viewing, at every width. They were
        // falling back to their own glyphs where the cell was tight — and
        // first-booking's glyph is a person with a plus, which Sir will not
        // have on the desk at all, while a lone eye does not say "viewing" to
        // anybody who has not been told (2026-08-27). If the words do not fit,
        // the names give way, not the words.
        const word = TEXT_BADGES[f.kind];
        if (word) {
          return (
            <span key={key} className={`bflag-new tint-${f.tint}`} title={title} aria-label={aria}>
              {word}
            </span>
          );
        }
        return (
          <span key={key} className={`bflag tint-${f.tint}`} title={title} aria-label={aria}>
            <Icon name={f.icon} size={size} />
          </span>
        );
      })}
      {probe}
      {hidden > 0 && (
        <span
          className="bflag bflag-more"
          title={flags.slice(shown.length).map((f) => `${f.label}: ${f.detail}`).join('\n')}
          aria-label={flags.slice(shown.length).map((f) => f.label).join(', ')}
        >
          +{hidden}
        </span>
      )}
    </span>
  );
}
